import { useEffect, useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Tabs,
    TabsList,
    TabsTrigger,
    TabsContent,
} from "@/components/ui/tabs";
import { toast } from "sonner";
import {
    BookOpen,
    ShoppingBag,
    User,
    ArrowRight,
    Clock,
    PlayCircle,
    Calendar,
    Lock,
    LogOut,
    Sparkles,
    ShieldCheck,
    MapPin,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import EnrollmentProfileForm, { isEnrollmentProfileComplete } from "@/components/EnrollmentProfileForm";
import FeedbackDialog from "@/components/FeedbackDialog";

function EnrollmentProfileTab() {
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);

    const reload = () => api.get("/me/profile").then(({ data }) => setProfile(data.profile)).catch(() => setProfile(null));
    useEffect(() => {
        reload().finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="text-center py-10 text-ink-700">Loading…</div>;
    const complete = isEnrollmentProfileComplete(profile);
    return (
        <div data-testid="account-profile-tab">
            {!complete && (
                <div className="mb-5 bg-accent-50 border border-accent-200 rounded-2xl p-4 flex items-start gap-3" data-testid="profile-incomplete-banner">
                    <ShieldCheck className="h-5 w-5 text-accent-700 mt-0.5 shrink-0" />
                    <div className="text-sm">
                        <div className="font-semibold text-ink-900">Complete your enrollment profile to book a course</div>
                        <p className="text-ink-700 mt-0.5">Online or offline — every course booking requires this profile to be filled once.</p>
                    </div>
                </div>
            )}
            {complete && (
                <div className="mb-5 bg-brand-50 border border-brand-100 rounded-2xl p-4 flex items-start gap-3" data-testid="profile-complete-banner">
                    <ShieldCheck className="h-5 w-5 text-brand-700 mt-0.5 shrink-0" />
                    <div className="text-sm">
                        <div className="font-semibold text-ink-900">Your enrollment profile is complete</div>
                        <p className="text-ink-700 mt-0.5">You're ready to enroll. Update any field below — changes save once you click Save.</p>
                    </div>
                </div>
            )}
            <EnrollmentProfileForm
                initial={profile}
                onSaved={(updated) => setProfile(updated)}
                submitLabel={complete ? "Update profile" : "Save profile"}
                showHeader={false}
            />
        </div>
    );
}

function MyMembershipTab() {
    const [status, setStatus] = useState(null);
    useEffect(() => {
        api.get("/me/membership").then(({ data }) => setStatus(data));
    }, []);
    if (status === null) return <div className="text-center py-10 text-ink-700">Loading…</div>;
    if (!status.active) {
        return (
            <div className="bg-white rounded-3xl border border-dashed border-gray-200 p-10 text-center" data-testid="account-no-membership">
                <Sparkles className="h-8 w-8 text-accent-500 mx-auto mb-3" />
                <h3 className="font-heading text-2xl">No active membership</h3>
                <p className="text-sm text-ink-700 mt-2">Subscribe to unlock members-only courses and member events.</p>
                <Button asChild className="rounded-full mt-5 bg-brand-500 hover:bg-brand-600 text-white shadow-cta">
                    <Link to="/membership">View plans</Link>
                </Button>
            </div>
        );
    }
    const m = status.membership;
    return (
        <div className="bg-white rounded-3xl border border-brand-200 bg-brand-50 p-8" data-testid="account-membership-card">
            <div className="flex items-center gap-3 mb-3">
                <ShieldCheck className="h-7 w-7 text-brand-700" />
                <div>
                    <div className="font-heading text-2xl">{m.plan_name}</div>
                    <div className="text-xs uppercase tracking-wide text-brand-700 font-semibold">Active</div>
                </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3 text-sm mt-5">
                <div><span className="text-ink-700">Started:</span> {String(m.started_at).slice(0, 10)}</div>
                <div><span className="text-ink-700">Expires:</span> {String(m.expires_at).slice(0, 10)}</div>
            </div>
        </div>
    );
}

function MyScheduleTab() {
    const [items, setItems] = useState(null);
    useEffect(() => {
        api.get("/me/schedule").then(({ data }) => setItems(data)).catch(() => setItems([]));
    }, []);
    if (items === null) return <div className="text-center py-10 text-ink-700">Loading…</div>;
    if (items.length === 0) {
        return (
            <div className="bg-white rounded-3xl border border-dashed border-gray-200 p-10 text-center" data-testid="account-no-schedule">
                <Calendar className="h-8 w-8 text-brand-500 mx-auto mb-3" />
                <h3 className="font-heading text-2xl">Nothing scheduled</h3>
                <p className="text-sm text-ink-700 mt-2">Sessions from courses you're enrolled in will appear here.</p>
            </div>
        );
    }
    return (
        <div className="space-y-2" data-testid="account-schedule-list">
            {items.map((s) => (
                <article key={s.id} className="rounded-2xl bg-white border border-gray-100 p-5 flex flex-wrap items-start justify-between gap-3" data-testid={`account-session-${s.id}`}>
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-[11px] uppercase tracking-wide rounded-full px-2 py-0.5 ${s.mode === "online" ? "bg-brand-50 text-brand-700" : "bg-accent-50 text-accent-700"}`}>{s.mode}</span>
                            <span className="font-semibold">{s.title}</span>
                        </div>
                        <div className="text-xs text-ink-700 mt-1">
                            <Link to={`/courses/${s.course_id}`} className="hover:text-brand-700">{s.course_title}</Link>
                        </div>
                        <div className="text-xs text-ink-700 mt-1 flex flex-wrap gap-3">
                            <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {new Date(s.start_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</span>
                            {s.instructor && <span>👤 {s.instructor}</span>}
                            {s.location_or_link && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {s.location_or_link}</span>}
                        </div>
                    </div>
                </article>
            ))}
        </div>
    );
}

function MyCoursesTab() {
    const [items, setItems] = useState(null);
    const [pending, setPending] = useState([]);
    const [fbOpen, setFbOpen] = useState(false);
    const [fbEnrollment, setFbEnrollment] = useState(null);

    const reloadPending = () => api.get("/me/pending-feedback").then(({ data }) => setPending(data || [])).catch(() => setPending([]));
    useEffect(() => {
        api.get("/me/enrollments")
            .then(({ data }) => setItems(data))
            .catch(() => setItems([]));
        reloadPending();
    }, []);

    if (items === null) {
        return (
            <div className="grid md:grid-cols-2 gap-5">
                {[0, 1, 2, 3].map((i) => (
                    <div key={i} className="h-44 rounded-2xl bg-gray-100 animate-pulse" />
                ))}
            </div>
        );
    }

    const PendingBanner = pending.length > 0 ? (
        <div className="mb-5 space-y-2" data-testid="pending-feedback-banner">
            {pending.map((p) => (
                <div key={p.id} className="bg-accent-50 border border-accent-200 rounded-2xl p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`pending-fb-${p.id}`}>
                    <div>
                        <div className="text-sm font-semibold text-ink-900">Share your feedback to complete {p.course_title}</div>
                        <p className="text-xs text-ink-700 mt-0.5">An admin marked this enrollment as completed on {new Date(p.marked_complete_at).toLocaleDateString()}. Please rate your experience.</p>
                    </div>
                    <Button
                        onClick={() => { setFbEnrollment(p); setFbOpen(true); }}
                        className="rounded-full bg-accent-500 hover:bg-accent-600 text-white shadow-accent px-5"
                        data-testid={`pending-fb-open-${p.id}`}
                    >
                        Give feedback
                    </Button>
                </div>
            ))}
        </div>
    ) : null;

    const FbDialog = (
        <FeedbackDialog
            open={fbOpen}
            onOpenChange={(o) => { setFbOpen(o); if (!o) reloadPending(); }}
            enrollment={fbEnrollment}
            onSubmitted={() => { reloadPending(); }}
        />
    );

    if (items.length === 0) {
        return (
            <div>
                {PendingBanner}
                {FbDialog}
                <div className="bg-white rounded-3xl border border-dashed border-gray-200 p-12 text-center">
                <div className="h-14 w-14 mx-auto rounded-2xl bg-brand-50 text-brand-600 flex items-center justify-center">
                    <BookOpen className="h-6 w-6" strokeWidth={1.5} />
                </div>
                <h3 className="mt-5 font-heading text-xl font-semibold text-ink-900">
                    No enrollments yet
                </h3>
                <p className="mt-2 text-ink-700 max-w-md mx-auto">
                    When you enroll in a course it'll show up here with your progress and
                    quick access links.
                </p>
                <Button
                    asChild
                    className="mt-6 rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta"
                    data-testid="account-courses-empty-cta"
                >
                    <Link to="/#courses">Browse Courses</Link>
                </Button>
            </div>
            </div>
        );
    }

    return (
        <div data-testid="account-enrollments-wrap">
            {PendingBanner}
            {FbDialog}
            <div className="grid md:grid-cols-2 gap-5" data-testid="account-enrollments">
            {items.map((e) => (
                <article
                    key={`${e.course_id}-${e.mode}`}
                    className="bg-white rounded-2xl border border-gray-100 shadow-soft overflow-hidden flex flex-col"
                    data-testid={`enrollment-${e.course_id}`}
                >
                    <div className="aspect-[16/9] overflow-hidden bg-paper-alt">
                        <img src={e.image_url} alt={e.title} className="w-full h-full object-cover" />
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span
                                className={`px-2.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${
                                    e.mode === "offline"
                                        ? "bg-accent-50 text-accent-700"
                                        : "bg-brand-50 text-brand-700"
                                }`}
                            >
                                {e.mode === "offline" ? "Offline · Studio" : "Online"}
                            </span>
                            {e.level && (
                                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-gray-100 text-ink-700">
                                    {e.level}
                                </span>
                            )}
                        </div>
                        <h3 className="mt-3 font-heading text-lg font-semibold text-ink-900">
                            {e.title}
                        </h3>
                        <div className="mt-2 flex flex-wrap gap-3 text-xs text-ink-700">
                            {e.duration && (
                                <span className="inline-flex items-center gap-1">
                                    <Clock className="h-3.5 w-3.5" /> {e.duration}
                                </span>
                            )}
                            {e.lessons_count > 0 && (
                                <span className="inline-flex items-center gap-1">
                                    <PlayCircle className="h-3.5 w-3.5" /> {e.lessons_count} lessons
                                </span>
                            )}
                            {e.paid_at && (
                                <span className="inline-flex items-center gap-1">
                                    <Calendar className="h-3.5 w-3.5" />
                                    Enrolled {new Date(e.paid_at).toLocaleDateString()}
                                </span>
                            )}
                        </div>
                        {/* Mock progress */}
                        <div className="mt-4">
                            <div className="flex items-center justify-between text-[11px] text-ink-700">
                                <span>Progress</span>
                                <span className="font-semibold text-ink-900">12%</span>
                            </div>
                            <div className="mt-1.5 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                                <div className="h-full w-[12%] bg-gradient-to-r from-brand-400 to-accent-400" />
                            </div>
                        </div>
                        <div className="mt-5 flex items-center justify-between gap-2">
                            <span className="text-xs text-ink-700 font-mono">
                                #{e.order_id?.slice(0, 8)}
                            </span>
                            <Button
                                asChild
                                className="rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta px-5 h-9 text-sm"
                                data-testid={`enrollment-continue-${e.course_id}`}
                            >
                                <Link to={`/courses/${e.course_id}`}>
                                    Continue <ArrowRight className="ml-1 h-3.5 w-3.5" />
                                </Link>
                            </Button>
                        </div>
                    </div>
                </article>
            ))}
            </div>
        </div>
    );
}

function MyOrdersTab() {
    const [items, setItems] = useState(null);
    useEffect(() => {
        api.get("/orders/my")
            .then(({ data }) => setItems(data))
            .catch(() => setItems([]));
    }, []);

    const statusColor = (s) =>
        ({
            paid: "bg-green-100 text-green-700",
            created: "bg-amber-100 text-amber-700",
            failed: "bg-red-100 text-red-700",
        }[s] || "bg-gray-100 text-gray-700");

    if (items === null) {
        return <div className="h-40 rounded-2xl bg-gray-100 animate-pulse" />;
    }

    if (items.length === 0) {
        return (
            <div className="bg-white rounded-3xl border border-dashed border-gray-200 p-12 text-center">
                <div className="h-14 w-14 mx-auto rounded-2xl bg-accent-50 text-accent-600 flex items-center justify-center">
                    <ShoppingBag className="h-6 w-6" strokeWidth={1.5} />
                </div>
                <h3 className="mt-5 font-heading text-xl font-semibold text-ink-900">
                    No orders yet
                </h3>
                <p className="mt-2 text-ink-700 max-w-md mx-auto">
                    Your purchases will appear here. Start by exploring our products.
                </p>
                <Button
                    asChild
                    className="mt-6 rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta"
                    data-testid="account-orders-empty-cta"
                >
                    <Link to="/#products">Shop Products</Link>
                </Button>
            </div>
        );
    }

    return (
        <ul className="space-y-4" data-testid="account-orders">
            {items.map((o) => (
                <li
                    key={o.id}
                    className="bg-white rounded-2xl border border-gray-100 shadow-soft p-5"
                    data-testid={`order-row-${o.id}`}
                >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                            <div className="text-xs text-ink-400 uppercase tracking-wider">
                                Order
                            </div>
                            <div className="font-mono text-sm font-semibold text-ink-900">
                                #{o.id.slice(0, 8)}
                            </div>
                            <div className="text-xs text-ink-700 mt-1">
                                {o.created_at && new Date(o.created_at).toLocaleString()}
                            </div>
                        </div>
                        <div className="text-right">
                            <span
                                className={`px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wide ${statusColor(o.status)}`}
                            >
                                {o.status}
                            </span>
                            <div className="mt-2 font-heading text-2xl font-semibold text-brand-600">
                                ₹{(o.subtotal || 0).toLocaleString("en-IN")}
                            </div>
                        </div>
                    </div>
                    <ul className="mt-4 grid sm:grid-cols-2 gap-2">
                        {(o.items || []).map((it, idx) => (
                            <li
                                key={`${o.id}-${idx}`}
                                className="flex items-center gap-3 bg-paper-alt rounded-xl p-2.5"
                            >
                                <div className="h-12 w-12 rounded-lg overflow-hidden shrink-0 bg-white border border-gray-100">
                                    <img
                                        src={it.image_url}
                                        alt={it.name}
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium text-ink-900 truncate">
                                        {it.name}
                                    </div>
                                    <div className="text-[11px] text-ink-700 flex items-center gap-1.5 mt-0.5">
                                        {it.kind === "course" ? (
                                            <span
                                                className={`px-1.5 py-0.5 rounded-full text-[9px] font-semibold uppercase tracking-wider ${
                                                    it.mode === "offline"
                                                        ? "bg-accent-50 text-accent-700"
                                                        : "bg-brand-50 text-brand-700"
                                                }`}
                                            >
                                                {it.mode === "offline" ? "Offline" : "Online"}
                                            </span>
                                        ) : (
                                            <span className="uppercase tracking-wider">
                                                {it.meta || "Product"}
                                            </span>
                                        )}
                                        <span>×{it.qty}</span>
                                    </div>
                                </div>
                                <div className="font-semibold text-sm text-ink-900">
                                    ₹{(it.price * it.qty).toLocaleString("en-IN")}
                                </div>
                            </li>
                        ))}
                    </ul>
                </li>
            ))}
        </ul>
    );
}

function SettingsTab() {
    const { user, refresh, logout } = useAuth();
    const navigate = useNavigate();
    const [profile, setProfile] = useState({ name: "", email: "" });
    const [savingProfile, setSavingProfile] = useState(false);
    const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
    const [savingPwd, setSavingPwd] = useState(false);

    useEffect(() => {
        if (user && typeof user === "object") {
            setProfile({ name: user.name || "", email: user.email || "" });
        }
    }, [user]);

    const onProfileSubmit = async (e) => {
        e.preventDefault();
        setSavingProfile(true);
        try {
            await api.patch("/auth/me", profile);
            await refresh();
            toast.success("Profile updated");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSavingProfile(false);
        }
    };

    const onPwdSubmit = async (e) => {
        e.preventDefault();
        if (pwd.new_password !== pwd.confirm) {
            toast.error("New passwords don't match");
            return;
        }
        setSavingPwd(true);
        try {
            await api.post("/auth/change-password", {
                current_password: pwd.current_password,
                new_password: pwd.new_password,
            });
            setPwd({ current_password: "", new_password: "", confirm: "" });
            toast.success("Password updated");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSavingPwd(false);
        }
    };

    return (
        <div className="grid lg:grid-cols-2 gap-6" data-testid="account-settings">
            {/* Profile */}
            <form
                onSubmit={onProfileSubmit}
                className="bg-white rounded-3xl border border-gray-100 shadow-soft p-6 md:p-8 space-y-4"
                data-testid="account-profile-form"
            >
                <h3 className="font-heading text-xl font-semibold text-ink-900">
                    Profile information
                </h3>
                <div>
                    <Label htmlFor="account-name">Name</Label>
                    <Input
                        id="account-name"
                        value={profile.name}
                        onChange={(e) => setProfile((p) => ({ ...p, name: e.target.value }))}
                        required
                        className="mt-1.5 rounded-xl"
                        data-testid="account-name-input"
                    />
                </div>
                <div>
                    <Label htmlFor="account-email">Email</Label>
                    <Input
                        id="account-email"
                        type="email"
                        value={profile.email}
                        onChange={(e) => setProfile((p) => ({ ...p, email: e.target.value }))}
                        required
                        className="mt-1.5 rounded-xl"
                        data-testid="account-email-input"
                    />
                </div>
                <Button
                    type="submit"
                    disabled={savingProfile}
                    className="rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta px-6"
                    data-testid="account-profile-save"
                >
                    {savingProfile ? "Saving…" : "Save changes"}
                </Button>
            </form>

            {/* Password */}
            <form
                onSubmit={onPwdSubmit}
                className="bg-white rounded-3xl border border-gray-100 shadow-soft p-6 md:p-8 space-y-4"
                data-testid="account-password-form"
            >
                <h3 className="font-heading text-xl font-semibold text-ink-900">
                    Change password
                </h3>
                <div>
                    <Label htmlFor="account-cur-pwd">Current password</Label>
                    <Input
                        id="account-cur-pwd"
                        type="password"
                        value={pwd.current_password}
                        onChange={(e) =>
                            setPwd((p) => ({ ...p, current_password: e.target.value }))
                        }
                        required
                        className="mt-1.5 rounded-xl"
                        data-testid="account-current-password"
                    />
                </div>
                <div>
                    <Label htmlFor="account-new-pwd">New password</Label>
                    <Input
                        id="account-new-pwd"
                        type="password"
                        minLength={6}
                        value={pwd.new_password}
                        onChange={(e) => setPwd((p) => ({ ...p, new_password: e.target.value }))}
                        required
                        className="mt-1.5 rounded-xl"
                        data-testid="account-new-password"
                    />
                </div>
                <div>
                    <Label htmlFor="account-confirm-pwd">Confirm new password</Label>
                    <Input
                        id="account-confirm-pwd"
                        type="password"
                        minLength={6}
                        value={pwd.confirm}
                        onChange={(e) => setPwd((p) => ({ ...p, confirm: e.target.value }))}
                        required
                        className="mt-1.5 rounded-xl"
                        data-testid="account-confirm-password"
                    />
                </div>
                <Button
                    type="submit"
                    disabled={savingPwd}
                    className="rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta px-6"
                    data-testid="account-password-save"
                >
                    <Lock className="h-4 w-4 mr-1.5" />
                    {savingPwd ? "Updating…" : "Update password"}
                </Button>
            </form>

            {/* Danger / sign out */}
            <div className="lg:col-span-2 bg-white rounded-3xl border border-gray-100 shadow-soft p-6 md:p-8 flex flex-wrap items-center justify-between gap-4">
                <div>
                    <h3 className="font-heading text-lg font-semibold text-ink-900">
                        Sign out
                    </h3>
                    <p className="text-sm text-ink-700">
                        End your session on this device.
                    </p>
                </div>
                <Button
                    onClick={async () => {
                        await logout();
                        navigate("/");
                    }}
                    variant="outline"
                    className="rounded-full border-2 border-accent-400 text-accent-600 hover:bg-accent-50 hover:text-accent-700 hover:border-accent-500 px-5"
                    data-testid="account-signout-btn"
                >
                    <LogOut className="h-4 w-4 mr-1.5" /> Sign out
                </Button>
            </div>
        </div>
    );
}

export default function AccountPage({ onOpenAuth }) {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center text-ink-700">
                Loading…
            </div>
        );
    }
    if (!user || typeof user !== "object") {
        return <Navigate to="/" replace />;
    }

    return (
        <div className="min-h-screen bg-paper-alt" data-testid="account-page">
            <Navbar onOpenAuth={onOpenAuth} />

            <header className="bg-gradient-to-br from-brand-500 to-brand-600 text-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
                    <div className="flex flex-col md:flex-row md:items-center gap-5 md:justify-between">
                        <div className="flex items-center gap-4">
                            <span className="h-14 w-14 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center text-white">
                                <User className="h-6 w-6" strokeWidth={1.6} />
                            </span>
                            <div>
                                <p className="text-[11px] uppercase tracking-[0.18em] text-white/70">
                                    Welcome back
                                </p>
                                <h1 className="font-heading text-3xl md:text-4xl font-semibold">
                                    {user.name?.split(" ")[0] || "Yogi"} 🙏
                                </h1>
                                <p className="text-sm text-white/80 mt-1">{user.email}</p>
                            </div>
                        </div>
                        <Button
                            asChild
                            className="rounded-full bg-white text-brand-700 hover:bg-white/90 shadow-soft"
                            data-testid="account-explore-courses"
                        >
                            <Link to="/#courses">
                                Explore Courses <ArrowRight className="ml-1 h-4 w-4" />
                            </Link>
                        </Button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
                <Tabs defaultValue="courses">
                    <TabsList className="bg-white border border-gray-100 p-1.5 rounded-full flex flex-wrap h-auto gap-1">
                        <TabsTrigger
                            value="courses"
                            className="rounded-full data-[state=active]:bg-brand-500 data-[state=active]:text-white px-4"
                            data-testid="account-tab-courses"
                        >
                            <BookOpen className="h-4 w-4 mr-1.5" /> My Courses
                        </TabsTrigger>
                        <TabsTrigger
                            value="schedule"
                            className="rounded-full data-[state=active]:bg-brand-500 data-[state=active]:text-white px-4"
                            data-testid="account-tab-schedule"
                        >
                            <Calendar className="h-4 w-4 mr-1.5" /> My Schedule
                        </TabsTrigger>
                        <TabsTrigger
                            value="orders"
                            className="rounded-full data-[state=active]:bg-brand-500 data-[state=active]:text-white px-4"
                            data-testid="account-tab-orders"
                        >
                            <ShoppingBag className="h-4 w-4 mr-1.5" /> Orders
                        </TabsTrigger>
                        <TabsTrigger
                            value="membership"
                            className="rounded-full data-[state=active]:bg-brand-500 data-[state=active]:text-white px-4"
                            data-testid="account-tab-membership"
                        >
                            <Sparkles className="h-4 w-4 mr-1.5" /> Membership
                        </TabsTrigger>
                        <TabsTrigger
                            value="profile"
                            className="rounded-full data-[state=active]:bg-brand-500 data-[state=active]:text-white px-4"
                            data-testid="account-tab-profile"
                        >
                            <ShieldCheck className="h-4 w-4 mr-1.5" /> Enrollment Profile
                        </TabsTrigger>
                        <TabsTrigger
                            value="settings"
                            className="rounded-full data-[state=active]:bg-brand-500 data-[state=active]:text-white px-4"
                            data-testid="account-tab-settings"
                        >
                            <User className="h-4 w-4 mr-1.5" /> Settings
                        </TabsTrigger>
                    </TabsList>
                    <TabsContent value="courses" className="mt-6">
                        <MyCoursesTab />
                    </TabsContent>
                    <TabsContent value="schedule" className="mt-6">
                        <MyScheduleTab />
                    </TabsContent>
                    <TabsContent value="orders" className="mt-6">
                        <MyOrdersTab />
                    </TabsContent>
                    <TabsContent value="membership" className="mt-6">
                        <MyMembershipTab />
                    </TabsContent>
                    <TabsContent value="profile" className="mt-6">
                        <EnrollmentProfileTab />
                    </TabsContent>
                    <TabsContent value="settings" className="mt-6">
                        <SettingsTab />
                    </TabsContent>
                </Tabs>
            </main>

            <Footer />
        </div>
    );
}
